import React, { FormEvent, useState } from "react";
import type { FetchBaseQueryError } from "@reduxjs/toolkit/query";
import { Toast, ToastContainer } from "react-bootstrap";
import FAQ from "../web/FAQ";
import { getApiErrorMessage } from "../../store/api/baseApi";
import { useCreateRequestMutation } from "../../store/api/requestsApi";
import { useGetSiteSettingsQuery, siteSettingsDefaults } from "../../store/api/siteSettingsApi";
import "./styles/ContactUsPage.css";

const Contactus = () => {
  const { data: site = siteSettingsDefaults } = useGetSiteSettingsQuery();
  const [createRequest, { isLoading }] = useCreateRequestMutation();
  const [showSuccessToast, setShowSuccessToast] = useState(false);
  const [showErrorToast, setShowErrorToast] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [validated, setValidated] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    message: "",
  });

  const handleReset = () => {
    setFormData({ name: "", email: "", phone: "", message: "" });
    setValidated(false);
  };

  const handleContactSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!event.currentTarget.checkValidity()) {
      event.stopPropagation();
      setValidated(true);
      return;
    }

    setValidated(true);
    try {
      await createRequest({
        name: formData.name,
        email: formData.email,
        phone: formData.phone,
        message: formData.message,
      }).unwrap();
      setErrorMessage("");
      setShowSuccessToast(true);
      setFormData({
        name: "",
        email: "",
        phone: "",
        message: "",
      });
      setValidated(false);
    } catch (apiError) {
      setErrorMessage(getApiErrorMessage(apiError as FetchBaseQueryError));
      setShowErrorToast(true);
    }
  };

  return (
    <section id='contact' className='contact-page website-page'>
      <div className='contact-hero website-page-hero text-center'>
        <div className='container'>
          <p className='contactus-kicker website-page-kicker mb-2'>
            Contact Dialin24
          </p>
          <h2 className='website-page-title mb-3'>
            Speak with the team handling planning, execution, and project
            coordination.
          </h2>
          <p className='contactus-subtitle website-page-subtitle mx-auto mb-0'>
            Tell us about your requirement and we will connect with you quickly
            for consultation, site discussion, or service support.
          </p>
        </div>
      </div>

      <div className='container website-page-content contact-content'>
        <div className='row g-3 mb-4'>
          <div className='col-md-4'>
            <div className='website-metric-card'>
              <h5>Quick Response</h5>
              <p>
                We respond promptly for project consultation, site queries, and
                service support.
              </p>
            </div>
          </div>
          <div className='col-md-4'>
            <div className='website-metric-card'>
              <h5>Structured Discussion</h5>
              <p>
                Share your site needs, expected scope, and timeline to get
                practical next-step guidance.
              </p>
            </div>
          </div>
          <div className='col-md-4'>
            <div className='website-metric-card'>
              <h5>Single Point Contact</h5>
              <p>
                Our team keeps communication clear from first inquiry to final
                project coordination.
              </p>
            </div>
          </div>
        </div>

        <div className='row g-4 mt-1'>
          <div className='col-lg-5'>
            <div className='card contact-card contact-card--brand h-100 border-0'>
              <div className='card-body p-4 p-md-5'>
                <h5 className='mb-3'>Get in Touch</h5>
                <p className='mb-4'>
                  You can reach us directly or submit a request using the form.
                </p>

                <ul className='list-unstyled mb-4 contact-list'>
                  <li>
                    <span className='contact-icon'>
                      <i className='bi bi-geo-alt-fill'></i>
                    </span>
                    <div>
                      <span className='contact-label'>Address</span>
                      <span>{site.address}</span>
                    </div>
                  </li>
                  <li>
                    <span className='contact-icon'>
                      <i className='bi bi-telephone-fill'></i>
                    </span>
                    <div>
                      <span className='contact-label'>Phone</span>
                      <span>{site.phone}</span>
                    </div>
                  </li>
                  <li>
                    <span className='contact-icon'>
                      <i className='bi bi-envelope-fill'></i>
                    </span>
                    <div>
                      <span className='contact-label'>Email</span>
                      <span>{site.email}</span>
                    </div>
                  </li>
                </ul>

                <div className='contact-hours'>
                  <h6 className='mb-3'>Business Hours</h6>
                  <p className='mb-2'>
                    <strong>Monday - Friday:</strong> {site.hoursWeekday}
                  </p>
                  <p className='mb-2'>
                    <strong>Saturday:</strong> {site.hoursSaturday}
                  </p>
                  <p className='mb-0'>
                    <strong>Project Support:</strong> {site.hoursSupport}
                  </p>
                </div>

                <div className='contact-actions mt-4'>
                  <a
                    href={`tel:${site.phone.replace(/[^\d+]/g, "")}`}
                    className='btn btn-sm contact-action-btn'
                  >
                    <i className='bi bi-telephone-outbound me-2'></i>
                    Call Now
                  </a>
                  <a
                    href={`mailto:${site.email}`}
                    className='btn btn-sm contact-action-btn'
                  >
                    <i className='bi bi-send me-2'></i>
                    Email Us
                  </a>
                  {site.whatsApp && (
                    <a
                      href={`https://wa.me/${site.whatsApp.replace(/[^\d]/g, "")}`}
                      className='btn btn-sm contact-action-btn'
                      target='_blank'
                      rel='noopener noreferrer'
                    >
                      <i className='bi bi-whatsapp me-2'></i>
                      WhatsApp
                    </a>
                  )}
                </div>
              </div>
            </div>
          </div>

          <div className='col-lg-7'>
            <div className='card contact-card contact-card--form h-100 border-0'>
              <div className='card-body p-4 p-md-5'>
                <div className='contact-form-header mb-4'>
                  <h5 className='mb-1'>Submit a Request</h5>
                  <p className='contact-note mb-0'>
                    Fill in your details and we'll get back to you shortly.
                  </p>
                </div>

                <form
                  noValidate
                  className={validated ? "was-validated" : ""}
                  onSubmit={handleContactSubmit}
                >
                  <div className='row g-3 mb-3'>
                    <div className='col-md-6'>
                      <label
                        htmlFor='name'
                        className='form-label contact-field-label'
                      >
                        <i className='bi bi-person-fill me-1'></i>Full Name{" "}
                        <span className='contact-required'>*</span>
                      </label>
                      <input
                        type='text'
                        className='form-control contact-input'
                        id='name'
                        placeholder='e.g. Rahul Sharma'
                        minLength={2}
                        value={formData.name}
                        onChange={(event) =>
                          setFormData((prev) => ({
                            ...prev,
                            name: event.target.value,
                          }))
                        }
                        required
                      />
                      <div className='invalid-feedback'>
                        Please enter your name.
                      </div>
                    </div>
                    <div className='col-md-6'>
                      <label
                        htmlFor='email'
                        className='form-label contact-field-label'
                      >
                        <i className='bi bi-envelope-fill me-1'></i>Email
                        Address <span className='contact-required'>*</span>
                      </label>
                      <input
                        type='email'
                        className='form-control contact-input'
                        id='email'
                        placeholder='you@example.com'
                        value={formData.email}
                        onChange={(event) =>
                          setFormData((prev) => ({
                            ...prev,
                            email: event.target.value,
                          }))
                        }
                        required
                      />
                      <div className='invalid-feedback'>
                        Please enter a valid email address.
                      </div>
                    </div>
                  </div>

                  <div className='mb-3'>
                    <label
                      htmlFor='phone'
                      className='form-label contact-field-label'
                    >
                      <i className='bi bi-telephone-fill me-1'></i>Phone Number{" "}
                      <span className='contact-required'>*</span>
                    </label>
                    <div className='input-group contact-input-group'>
                      <span className='input-group-text contact-input-prefix'>
                        +91
                      </span>
                      <input
                        type='tel'
                        className='form-control contact-input'
                        id='phone'
                        placeholder='10-digit mobile number'
                        pattern='[0-9]{10}'
                        minLength={10}
                        maxLength={10}
                        value={formData.phone}
                        onChange={(event) =>
                          setFormData((prev) => ({
                            ...prev,
                            phone: event.target.value,
                          }))
                        }
                        required
                      />
                      <div className='invalid-feedback'>
                        Enter a valid 10-digit phone number.
                      </div>
                    </div>
                  </div>

                  <div className='mb-4'>
                    <label
                      htmlFor='message'
                      className='form-label contact-field-label'
                    >
                      <i className='bi bi-chat-left-text-fill me-1'></i>Your
                      Requirement <span className='contact-required'>*</span>
                    </label>
                    <textarea
                      className='form-control contact-input'
                      id='message'
                      rows={4}
                      placeholder='Describe your project, location, or any specific requirement…'
                      minLength={10}
                      value={formData.message}
                      onChange={(event) =>
                        setFormData((prev) => ({
                          ...prev,
                          message: event.target.value,
                        }))
                      }
                      required
                    />
                    <div className='invalid-feedback'>
                      Please enter at least 10 characters.
                    </div>
                  </div>

                  <div className='contact-form-actions'>
                    <button
                      type='submit'
                      className='btn contact-submit-btn'
                      disabled={isLoading}
                    >
                      {isLoading ? (
                        <>
                          <span
                            className='spinner-border spinner-border-sm me-2'
                            role='status'
                            aria-hidden='true'
                          />
                          Sending…
                        </>
                      ) : (
                        <>
                          <i className='bi bi-send-fill me-2'></i>Send Request
                        </>
                      )}
                    </button>
                    <button
                      type='button'
                      className='btn contact-reset-btn'
                      onClick={handleReset}
                      disabled={isLoading}
                    >
                      <i className='bi bi-arrow-counterclockwise me-2'></i>Reset
                    </button>
                  </div>

                  <p className='contact-note mt-3 mb-0'>
                    <i
                      className='bi bi-shield-check me-1'
                      style={{ color: "#352672" }}
                    ></i>
                    We respect your privacy and only use your details to respond
                    to your request.
                  </p>
                </form>
              </div>
            </div>
          </div>
        </div>

        <div className='row g-4 mt-1'>
          <div className='col-lg-7'>
            <div className='card contact-card map-card border-0'>
              <div className='card-body p-3 p-md-4'>
                <h5 className='mb-3'>
                  <i className='bi bi-pin-map-fill me-2'></i>
                  Our Location
                </h5>
                <div className='ratio ratio-4x3'>
                  <iframe
                    src={site.mapEmbedUrl || "https://www.google.com/maps?q=Lucknow,+Uttar+Pradesh&output=embed"}
                    title='Google Map'
                    allowFullScreen
                    loading='lazy'
                    style={{ border: 0 }}
                  ></iframe>
                </div>
              </div>
            </div>
          </div>
          <div className='col-lg-5'>
            <div className='card contact-card faq-card h-100 border-0'>
              <div className='card-body p-3 p-md-4'>
                <FAQ />
              </div>
            </div>
          </div>
        </div>
      </div>
      <ToastContainer position='bottom-end' className='p-3'>
        <Toast
          show={showSuccessToast}
          onClose={() => setShowSuccessToast(false)}
          delay={3000}
          autohide
          bg='success'
        >
          <Toast.Header closeButton>
            <strong className='me-auto'>Request Sent</strong>
          </Toast.Header>
          <Toast.Body className='text-white'>
            Success! Your request has been submitted.
          </Toast.Body>
        </Toast>
        <Toast
          show={showErrorToast}
          onClose={() => setShowErrorToast(false)}
          delay={4000}
          autohide
          bg='danger'
        >
          <Toast.Header closeButton>
            <strong className='me-auto'>Request Failed</strong>
          </Toast.Header>
          <Toast.Body className='text-white'>
            {errorMessage ||
              "Unable to submit request right now. Please try again."}
          </Toast.Body>
        </Toast>
      </ToastContainer>
    </section>
  );
};

export default Contactus;
